<template>
  <nuxt-link :to="getBranchURL()" :target="isListPages ? '_blank' : '_self'" @click="pushGtmSource">
    <v-card class="product-card" elevation="0">
      <div class="cover-block">
        <v-img :src="getImage(branch.cover_image)" cover :aspect-ratio="16 / 9" gradient="to bottom, rgba(0, 0, 0, 0), rgba(0, 0, 0, 0.4)" />

        <div class="tags-bar">
          <v-chip v-for="(tagChip, idx) in tagChips" :key="idx" class="tag-chip" :class="tagChip.className" label>
            <FunnowSvgIcon v-if="tagChip.iconFilename" class="wd-mr-1 wd-text-gray10" max-width="16" :image-path="tagChip.iconFilename" />
            {{ tagChip.text }}
          </v-chip>
        </div>
      </div>
      <div class="px-2 pt-3 product-info">
        <div class="card-title mb-2 d-flex justify-space-between align-center product-line">
          <h3 class="branch-name mb-0 text-truncate" :data-testid="testIDs.productCard.branchName">{{ branch.name }}</h3>
          <p class="price mb-0" v-html="branchMinPrice"></p>
        </div>

        <div class="d-inline-flex flex-wrap align-center product-line justify-space-between">
          <div class="rating-text d-flex align-center">
            <funnow-icon class="wd-mr-1 wd-w-4" max-width="20" :image-path="'star_orange.svg'" />
            <span class="mr-1"> {{ branch.rating }} </span>
            <span :data-testid="testIDs.productCard.reviewCount"> ({{ branch.number_of_comments }}) </span>
          </div>

          <!-- 最早可預訂時間 -->
          <v-chip v-if="sellTypeChip" label class="tag" :class="[`tag--${sellTypeChip.classType}`]">
            <funnow-icon v-if="branch.sell_time_type === 'now'" class="wd-mr-1 wd-w-4 !wd-min-w-0" max-width="16" :image-path="'flash-icon.svg'" />
            <span :style="{ overflow: 'hidden' }"> {{ sellTypeChip.text }} </span>
          </v-chip>
        </div>
      </div>
    </v-card>
  </nuxt-link>
</template>

<script>
import { mapGetters } from 'vuex'
import utils from '@/utils'
import testIDs from '@/constants/test-ids.json'

import FunnowSvgIcon from '@/components/icons/funnow-svg-icon.vue'
import FunnowIcon from '@/components/icons/funnow-icon'
import { useGtmSourceInject, pushGtmSource as pushGtmSourceToDataLayer } from '@/composition/useGtmSource'

export default {
  name: 'BranchCard',
  components: {
    FunnowSvgIcon,
    FunnowIcon,
  },
  props: {
    branch: { type: Object, default: () => ({}) },
    bringQuery: { type: Boolean, default: true },
  },
  setup() {
    const gtmSource = useGtmSourceInject()
    const pushGtmSource = () => {
      if (gtmSource) {
        pushGtmSourceToDataLayer(gtmSource)
      }
    }
    const localePath = useLocalePath()
    const route = useRoute()

    const isListPages = computed(
      () =>
        checkIfRegionCategoriesPage(route.name) ||
        checkIfRegionThemesPage(route.name) ||
        checkIfRegionSearchPage(route.name) ||
        checkIfSubCategoryPage(route.name)
    )

    return {
      testIDs,
      pushGtmSource,
      localePath,
      isListPages,
    }
  },
  computed: {
    tagChips() {
      const tagChips = []
      const tagType = {
        limited: { className: ['limited-tag'], text: this.$t('tag_limited') },
        flashSale: { className: ['flash-sale-tag'], text: this.$t('tag_flash_sale_now'), iconFilename: 'promo_code.svg' },
        hotSale: { className: ['hot-tag'], text: this.$t('tag_hot') },
        newSale: { className: ['new-tag'], text: this.$t('tag_new') },
        vip: { className: ['vip-tag'], text: this.$t('tag_special_gift'), iconFilename: 'gift.svg' },
        elite: { className: ['elite-tag'], text: this.$t('tag_elite') },
      }

      // 限量釋出
      if (this.branch.is_limited) {
        tagChips.push(tagType.limited)
        return tagChips
      }

      // 限時促銷
      if (this.branch.flash_time_type && this.branch.flash_time_type !== 'hidden') {
        switch (this.branch.flash_time_type) {
          case 'day': {
            const date = this.$dayjs(this.branch.flash_time).format('MM/DD')
            tagType.flashSale.text = this.$t('tag_flash_sale_time', [date])
            break
          }
          case 'hour': {
            const time = this.$dayjs(this.branch.flash_time).format('HH:mm')
            tagType.flashSale.text = this.$t('tag_flash_sale_time', [time])
            break
          }
        }
        tagChips.push(tagType.flashSale)
        return tagChips
      }

      if (this.branch.is_elite) {
        tagChips.push(tagType.elite)
      }
      if (this.branch.is_hot) {
        tagChips.push(tagType.hotSale)
      }
      // 分店小卡沒有白金會員和新上架 tag
      // if (this.branch.vip_reward) { tagChips.push(tagType.vip) }
      // if (this.branch.is_new_product) { tagChips.push(tagType.newSale) }
      return tagChips
    },
    sellTypeChip() {
      if (this.branch.onboard === 0) {
        return { text: this.$t('all_sold_out'), classType: 'closed' }
      } else {
        switch (this.branch.sell_time_type) {
          case 'now':
            return { text: this.$t('package_available_now'), classType: 'now' }
          case 'hidden':
            return false
          case 'day':
          case 'hour': {
            const dateForamt = this.branch.sell_time_type === 'day' ? 'MM/DD' : 'HH:mm'
            const sellDateTime = this.$dayjs(this.branch.sell_time).format(dateForamt)
            const dayText = this.$t('package_start_booking_time', [sellDateTime])
            return { text: dayText, classType: 'day' }
          }
          default:
            return false
        }
      }
    },
    branchMinPrice() {
      const price = this.changeThousandsSeparators(this.branch.min_price)
      const textHtml = `${this.currency} <span class="price-text wd-text-orange50" data-testid="${this.testIDs.productCard.price}"> ${price} </span>`
      const discountText = this.$t('price_up', [textHtml])
      return discountText
    },
    // coverImage () {
    //   return `${utils.CDN}${utils.imagePath(this.branch.cover_image)}`
    // },
    currency() {
      return (
        this.allCurrency.filter(obj => {
          return obj.id === this.branch.currency_id
        })?.[0]?.code || ''
      )
    },
    ...mapGetters({
      allCurrency: 'currency/allCurrency',
    }),
  },
  methods: {
    changeThousandsSeparators(number) {
      const price = parseFloat(number)
      if (price) {
        return price.toLocaleString()
      }
      return number
    },
    getImage(path) {
      return utils.imageUrl(path)
    },
    getBranchURL() {
      const routerQuery = this.bringQuery ? this.$route.query : {}
      return this.localePath({ name: 'branches-id', params: { id: this.branch.bid }, query: routerQuery })
    },
  },
}
</script>

<style scoped lang="scss" src="@/assets/sass/components/card/product.scss"></style>
